import { getNextPanDomain } from '../../../../core/components/zoom/utils/get-next-pan-domain.js';
import type { AnyNumberScale } from '../../../../core/types/scales.js';
import { ZOOM_CHANGE_TYPE } from '../../../../core/types/zoom.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { reducePanDomain } from '../../../store/reducer-pan.js';
import { useSetState } from '../../../store/selectors.js';
import { type XYChartState } from '../../../types/state.js';
import {
  isInfiniteZoomEnabledFor,
  isPanEnabledFor,
} from '../../../utils/axis-interactions.js';
import { getCurrentScale, getInitialScale } from '../../../utils/get-scale.js';
import { handleZoomChange } from '../utils/zoom-change-handler.js';

/**
 * Returns a callback which given a delta in pixels, will apply the pan interaction and update the state accordingly
 */
export const useApplyPanDelta = () => {
  const setState = useSetState();
  const axisIdToScales = useXYChartScales();
  const { axes } = useXYChartData();

  return (delta: number) => {
    const newCurrentDomain: XYChartState['currentDomain'] = new Map();

    for (const axis of axes) {
      let nextDomain;

      if (isPanEnabledFor(axis)) {
        const currentScale = getCurrentScale(axisIdToScales, axis.id);
        const initialScale = getInitialScale(axisIdToScales, axis.id);

        const initialDomain = initialScale.domain();

        const [min, max] = getNextPanDomain(
          [+initialDomain[0], +initialDomain[1]],
          currentScale as AnyNumberScale,
          -delta,
          false,
          isInfiniteZoomEnabledFor(axis),
        );

        nextDomain = [min, max];

        handleZoomChange(axis, min, max, ZOOM_CHANGE_TYPE.Panning);
      }

      newCurrentDomain.set(
        axis.id,
        nextDomain ? { min: nextDomain[0], max: nextDomain[1] } : undefined,
      );
    }

    setState(reducePanDomain(newCurrentDomain));
  };
};
